import {
  setCurrentStep,
  setFormData,
  setValidation
} from '@/store/slices/rewardsSlice';
import {ROUTES} from '@/utils/constants/routesConstants';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {saveRewardDraft, publishReward, revokeReward} from '@/services/rewards';
import {APPLICATION_ID} from '@/utils/constants/globalConstants';
import {
  validateRewardNameIfNeeded,
  showToast,
  withLoadingAndErrorHandling
} from './rewardHandlerUtils';

/**
 * Creates a handlePublish function for reward flows
 * @param {Object} params - Handler parameters
 * @param {Function} params.buildPayload - Function to build the payload
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - Navigation function
 * @returns {Function} handlePublish function
 */
export const createHandlePublish = ({buildPayload, dispatch, navigate}) => {
  return async () => {
    await withLoadingAndErrorHandling({
      dispatch,
      operation: async () => {
        const payload = buildPayload('PUBLISHED');
        await publishReward(payload);
        showToast(dispatch, 'Reward published successfully', 'success');
        navigate(ROUTES.REWARD_CATALOG);
      },
      onError: () => {
        showToast(dispatch, 'Failed to publish reward', 'error');
      },
      errorMessage: 'Failed to publish reward'
    });
  };
};

/**
 * Creates a handlePrevious function for reward flows
 * @param {Object} params - Handler parameters
 * @param {number} params.currentStep - Current step number
 * @param {Function} params.dispatch - Redux dispatch function
 * @returns {Function} handlePrevious function
 */
export const createHandlePrevious = ({currentStep, dispatch}) => {
  return () => {
    if (currentStep > 1) {
      dispatch(setCurrentStep(currentStep - 1));
    }
  };
};

/**
 * Creates a handleNext function for reward flows
 * @param {Object} params - Handler parameters
 * @param {number} params.currentStep - Current step number
 * @param {Object} params.formData - Form data object
 * @param {Function} params.validateCurrentStep - Function to validate current step
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.handlePublish - Function to handle publishing
 * @param {boolean} params.shouldValidateName - Whether to validate name (for create flow)
 * @returns {Function} handleNext function
 */
export const createHandleNext = ({
  currentStep,
  formData,
  validateCurrentStep,
  dispatch,
  handlePublish,
  shouldValidateName = false
}) => {
  return async () => {
    // For step 1, validate name duplication via API first (only for new rewards)
    const nameValidationResult = await validateRewardNameIfNeeded({
      shouldValidate: shouldValidateName,
      currentStep,
      formData
    });

    if (nameValidationResult?.isDuplicateName) {
      return {success: false, isDuplicateName: true};
    }

    if (validateCurrentStep()) {
      if (currentStep < 4) {
        dispatch(setCurrentStep(currentStep + 1));
      } else {
        await handlePublish();
      }
    }
  };
};

/**
 * Creates a handleDraft function for reward flows
 * @param {Object} params - Handler parameters
 * @param {number} params.currentStep - Current step number
 * @param {Object} params.formData - Form data object
 * @param {Function} params.buildPayload - Function to build the payload
 * @param {Function} params.loadDraft - Function to load draft
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - Navigation function
 * @param {boolean} params.shouldValidateName - Whether to validate name (for create flow)
 * @returns {Function} handleDraft function
 */
export const createHandleDraft = ({
  currentStep,
  formData,
  buildPayload,
  loadDraft,
  dispatch,
  navigate,
  shouldValidateName = false
}) => {
  return async ({redirectOnSuccess = true} = {}) => {
    // For setup step draft saves, require name and reward type and surface inline errors
    if (currentStep === 1) {
      const isNameMissing = !formData?.name?.trim?.();
      const isRewardTypeMissing = !formData?.rewardType?.trim?.();

      if (isNameMissing || isRewardTypeMissing) {
        dispatch(
          setValidation({
            nameValid: isNameMissing,
            rewardTypeValid: isRewardTypeMissing
          })
        );
        return {success: false, hasRequiredFieldErrors: true};
      }
    }

    // For step 1, validate name duplication via API first (only for new rewards)
    const nameValidationResult = await validateRewardNameIfNeeded({
      shouldValidate: shouldValidateName,
      currentStep,
      formData
    });

    if (nameValidationResult?.isDuplicateName) {
      return {success: false, isDuplicateName: true};
    }

    await withLoadingAndErrorHandling({
      dispatch,
      operation: async () => {
        const payload = buildPayload(REWARD_STATUS.DRAFT);
        const resp = await saveRewardDraft(payload);
        const respData = resp?.data || resp;
        const returnedId = respData?.id || respData?.rewardId;
        if (
          returnedId &&
          String(returnedId) !== String(formData.rewardId || '')
        ) {
          dispatch(setFormData({rewardId: String(returnedId)}));
        }
        const effectiveId = returnedId || formData?.rewardId;
        if (effectiveId) {
          await loadDraft(effectiveId);
        }
        showToast(dispatch, 'Reward saved as draft successfully', 'success');

        if (redirectOnSuccess) {
          navigate(ROUTES.REWARD_CATALOG);
        }
      },
      onError: () => {
        showToast(dispatch, 'Failed to save draft', 'error');
      },
      errorMessage: 'Failed to save draft'
    });
  };
};

/**
 * Creates a handleRevoke function for reward flows
 * @param {Object} params - Handler parameters
 * @param {Object} params.formData - Form data object
 * @param {string} params.countryCode - Country code
 * @param {Function} params.dispatch - Redux dispatch function
 * @returns {Function} handleRevoke function
 */
export const createHandleRevoke = ({formData, countryCode, dispatch}) => {
  return async () => {
    if (!formData?.rewardId) {
      showToast(dispatch, 'Reward ID is required to revoke', 'error');
      return;
    }

    await withLoadingAndErrorHandling({
      dispatch,
      operation: async () => {
        const applicationId = APPLICATION_ID[countryCode] || APPLICATION_ID.MX;

        const payload = {
          id: Number(formData.rewardId),
          applicationId: applicationId
        };

        const resp = await revokeReward(payload);

        if (resp?.success === true) {
          dispatch(setFormData({status: REWARD_STATUS.REVOKED}));
          showToast(dispatch, 'Reward revoked successfully', 'success');
        } else {
          throw new Error(resp?.message || 'Failed to revoke reward');
        }
      },
      onError: (errorMsg) => {
        showToast(dispatch, errorMsg || 'Failed to revoke reward', 'error');
      },
      errorMessage: 'Failed to revoke reward'
    });
  };
};
